import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Select, { components } from 'react-select'
import { normalizeForFilter } from 'helpers/prepares'
import Checkbox from 'ui/components/forms/Checkbox'
import Icon from 'ui/components/Icon'
import Scrollbar from 'ui/components/Scrollbar'
import {
  CLASSNAME_PREFIX,
  SELECT_INPUT_ACTIONS,
  CLEAR_INPUT_ACTIONS,
  IS_APPLE_DEVICE,
} from './constants'
import {
  StyledOption,
  OptionText,
  SelectWrap,
  SelectLabel,
  SelectError,
  HorizontalValueContainerWrap,
  ScrollableValueContainerWrap,
  createCustomStyles,
} from './styled'

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon name="arrow-down" size="24" />
    </components.DropdownIndicator>
  )
}

const Menu = ({ children, selectProps: { maxHeight }, ...props }) => {
  return (
    <components.Menu {...props}>
      <Scrollbar maxHeight={maxHeight}>{children}</Scrollbar>
    </components.Menu>
  )
}

const Option = ({ isSelected, value, ...props }) => {
  return (
    <components.Option {...props}>
      <StyledOption title={value}>
        <Checkbox checked={isSelected} onChange={() => null} />{' '}
        <OptionText>{value}</OptionText>
      </StyledOption>
    </components.Option>
  )
}

const MultiValue = ({ children, ...props }) => {
  return <components.MultiValue {...props}>{children}</components.MultiValue>
}

const HorizontalValueContainer = ({ children, ...props }) => {
  const scrollRef = useRef(null)

  useEffect(() => {
    if (IS_APPLE_DEVICE) return

    const node = scrollRef.current
    if (!node) return

    const handleWheel = (event) => {
      const { deltaY } = event
      const hasHorizontalScroll = node.scrollWidth > node.clientWidth

      if (!hasHorizontalScroll || !deltaY) return

      event.preventDefault()
      event.stopPropagation()

      const maxScrollLeft = node.scrollWidth - node.clientWidth
      const nextScrollLeft = node.scrollLeft + deltaY

      node.scrollLeft = Math.max(0, Math.min(nextScrollLeft, maxScrollLeft))
    }

    node.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      node.removeEventListener('wheel', handleWheel)
    }
  }, [])

  return (
    <components.ValueContainer {...props}>
      <HorizontalValueContainerWrap ref={scrollRef}>
        {children}
      </HorizontalValueContainerWrap>
    </components.ValueContainer>
  )
}

const ScrollableValueContainer = ({ children, ...props }) => {
  const currentValues = props.getValue()
  const simpleBarRef = useRef(null)
  const previousValuesLengthRef = useRef(currentValues.length)

  useEffect(() => {
    const simpleBar = simpleBarRef.current
    const isValueAdded = currentValues.length > previousValuesLengthRef.current

    if (simpleBar && isValueAdded) {
      const scrollElement = simpleBar.getScrollElement?.()

      requestAnimationFrame(() => {
        scrollElement?.scrollTo({
          top: simpleBar.contentEl?.scrollHeight ?? scrollElement.scrollHeight,
          behavior: 'smooth',
        })
      })
    }

    previousValuesLengthRef.current = currentValues.length
  }, [currentValues.length])

  return (
    <components.ValueContainer {...props}>
      <ScrollableValueContainerWrap $menuIsOpen={props.selectProps.menuIsOpen}>
        <Scrollbar maxHeight={120} width="100%" ref={simpleBarRef}>
          {children}
        </Scrollbar>
      </ScrollableValueContainerWrap>
    </components.ValueContainer>
  )
}

const SelectWithCheckboxes = ({
  label,
  meta,
  error,
  required,
  upperLabel = true,
  isMulti = true,
  id,
  instanceId,
  keepInputOnSelect = false,
  onInputChange,
  scrollableContent,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')

  const isTouched = meta && meta.touched
  const hasError = isTouched && meta.error

  const handleInputChange = (newValue, actionMeta) => {
    onInputChange?.(newValue, actionMeta)

    switch (actionMeta.action) {
      case SELECT_INPUT_ACTIONS.INPUT_CHANGE:
        setInputValue(newValue)
        return newValue

      case SELECT_INPUT_ACTIONS.SET_VALUE:
        return inputValue

      default:
        if (CLEAR_INPUT_ACTIONS.includes(actionMeta.action)) {
          setInputValue('')
          return ''
        }

        return inputValue
    }
  }

  const inputControlProps = keepInputOnSelect
    ? {
        inputValue,
        onInputChange: handleInputChange,
      }
    : {
        ...(onInputChange && { onInputChange }),
      }

  const ValueContainer = scrollableContent
    ? ScrollableValueContainer
    : HorizontalValueContainer

  return (
    <SelectWrap
      className={classNames({
        'has-error': hasError,
      })}
    >
      {label && upperLabel && (
        <SelectLabel>
          {label} {required && <span>*</span>}
        </SelectLabel>
      )}
      <Select
        isMulti={isMulti}
        components={{
          Option,
          MultiValue,
          DropdownIndicator,
          Menu,
          ValueContainer,
        }}
        styles={createCustomStyles(scrollableContent)}
        noOptionsMessage={() => 'Не найдено'}
        closeMenuOnSelect={false}
        captureMenuScroll={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        classNamePrefix={CLASSNAME_PREFIX}
        placeholder={`${label}${required ? '*' : ''}`}
        filterOption={(option, inputValue) =>
          normalizeForFilter(option.value, inputValue) ? option.data : ''
        }
        required={required}
        instanceId={instanceId || id}
        {...inputControlProps}
        {...props}
      />
      {hasError && <SelectError>{error}</SelectError>}
    </SelectWrap>
  )
}

export default SelectWithCheckboxes
